'use client'

import { useTranslations } from 'next-intl'

import { signUp as _signUp } from '@/entities/auth'
import { JoinType, TSignUp, TSignUpResponse } from '@/entities/auth'
import { CERT, MessageType, ResponseCode, USER, VALID } from '@/shared/api'
import { useAirbridgeContext } from '@/shared/config'
import { useAction } from '@/shared/config/action'
import { useServerErrorHandler } from '@/shared/config/action'
import { signInWithToken } from '@/shared/lib'

const EXCLUDED_RETRY_CODES: ResponseCode[] = [
  VALID['001'],
  USER['001'],
  USER['002'],
  USER['003'],
  USER['004'],
  USER['005'],
  USER['006'],
  USER['007'],
  USER['008'],
  CERT['010'],
]

export const useSignUp = ({
  joinType,
  onSuccess,
}: {
  joinType: JoinType
  onSuccess: (ocrToken?: string) => void
}) => {
  const t = useTranslations()

  const { handleServerError } = useServerErrorHandler()

  const { sendEvent } = useAirbridgeContext()

  const sendAirbridgeEvent = (data: TSignUpResponse, ocrToken?: string) => {
    const { airbridgeData } = data

    const params = {
      action: airbridgeData.action,
      label: airbridgeData.label,
      value: airbridgeData.value,
    }

    sendEvent({
      event: airbridgeData.event_name,
      params,
      onSuccess: () => {
        onSuccess(ocrToken)
      },
    })
  }

  const action = useAction(_signUp, {
    onSuccess: async ({ data, input }) => {
      if (data) {
        sendAirbridgeEvent(data, input.body.ocrToken)
        await signInWithToken(data.firebaseToken)
      }
    },
    onError: ({ error: { serverError }, input }) => {
      if (!serverError) return

      if (serverError.code === VALID['001'] && serverError.data) {
        const errorDetails = Object.values(serverError.data).join('\n')

        handleServerError({
          ...serverError,
          title: serverError.content,
          content: errorDetails,
        })

        return
      }

      if (!EXCLUDED_RETRY_CODES.includes(serverError.code)) {
        handleServerError(
          {
            ...serverError,
            type: MessageType.CONFIRM,
            title: t('signup_complete.dialog.error.title'),
            content: t('signup_complete.dialog.error.description'),
          },
          {
            cancelText: t('common.button.close'),
            confirmText: t('signup_complete.dialog.error.button.retry'),
            onConfirm: () => {
              action.execute({ joinType, body: input.body })
            },
          },
        )

        return
      }

      if (serverError.code) handleServerError(serverError)
    },
  })

  const signUp = (data: TSignUp) => {
    action.execute({ joinType, body: data })
  }

  return { signUp }
}
